/**
 * Deferred Image Loader
 *
 * Implements strict lazy loading for below-the-fold images using IntersectionObserver.
 * Images with data-deferred-src will only load when approaching the viewport.
 *
 * Usage:
 * <img
 *   src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3C/svg%3E"
 *   data-deferred-src="actual-image.jpg"
 *   data-deferred-srcset="image-400.jpg 400w, image-800.jpg 800w"
 *   data-deferred-sizes="(max-width: 749px) 100vw, 50vw"
 *   width="800"
 *   height="600"
 *   alt="Description"
 * />
 */

const PLACEHOLDER_SRC =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3C/svg%3E"

/** @type {number} */
export const DEFERRED_IMAGE_MAX_CONCURRENT = 3

/** @type {number} */
export const DEFERRED_IMAGE_MAX_CONCURRENT_FAST = 6

/**
 * Delay (ms) before retrying a deferred image that failed to load once.
 * @type {number}
 */
export const DEFERRED_IMAGE_RETRY_DELAY = 500

/**
 * @returns {number}
 */
export function getDeferredImageMaxConcurrent() {
  if (typeof navigator !== 'undefined' && navigator.connection?.saveData) {
    return DEFERRED_IMAGE_MAX_CONCURRENT
  }

  const effectiveType = navigator.connection?.effectiveType
  if (effectiveType === '2g' || effectiveType === '3g') {
    return DEFERRED_IMAGE_MAX_CONCURRENT
  }

  return DEFERRED_IMAGE_MAX_CONCURRENT_FAST
}

/**
 * Responsive root margin for deferred image IntersectionObserver.
 * @returns {string}
 */
export function getDeferredImageRootMargin() {
  if (typeof navigator !== 'undefined' && navigator.connection?.saveData) {
    return '100px 0px'
  }

  if (typeof window !== 'undefined') {
    const width = window.innerWidth
    if (width < 750) return '200px 0px'
    if (width < 990) return '400px 0px'
  }

  return '600px 0px'
}

/**
 * @param {'high' | 'default'} [priority='default']
 * @returns {string}
 */
export function getDeferredImageRootMarginForPriority(priority = 'default') {
  if (priority === 'high') {
    return getDeferredImageRootMargin()
  }

  if (typeof navigator !== 'undefined' && navigator.connection?.saveData) {
    return '100px 0px'
  }

  if (typeof window !== 'undefined') {
    const width = window.innerWidth
    if (width < 750) return '120px 0px'
    if (width < 990) return '200px 0px'
  }

  return '300px 0px'
}

/**
 * @param {HTMLImageElement} img
 * @returns {boolean}
 */
export function isDeferredCloneImage(img) {
  return Boolean(
    img.closest('[data-clone]') ||
    img.closest('[data-repeat]') ||
    img.closest('[data-creator-marquee-clone]')
  )
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export function canonicalDeferredSrcMatches(a, b) {
  if (!a || !b) return false
  if (a === b) return true

  try {
    return (
      new URL(a, document.baseURI).href === new URL(b, document.baseURI).href
    )
  } catch {
    return false
  }
}

/**
 * @param {HTMLImageElement} img
 * @param {string} canonicalSrc
 * @returns {boolean}
 */
function deferredSrcMatches(img, canonicalSrc) {
  return canonicalDeferredSrcMatches(img.src, canonicalSrc)
}

/**
 * @param {string} canonicalSrc
 * @returns {HTMLImageElement | null}
 */
export function findLoadedDeferredImageForCanonical(canonicalSrc) {
  for (const node of document.querySelectorAll(
    'img[data-deferred-loaded="true"]'
  )) {
    if (!(node instanceof HTMLImageElement)) continue
    if (isDeferredCloneImage(node)) continue

    const nodeCanonical = node.dataset.deferredCanonicalSrc
    if (
      nodeCanonical &&
      canonicalDeferredSrcMatches(nodeCanonical, canonicalSrc)
    ) {
      return node
    }

    if (deferredSrcMatches(node, canonicalSrc)) return node
  }

  return null
}

/**
 * @param {HTMLImageElement} mirrorImg
 * @returns {boolean}
 */
export function hydrateDeferredMirror(mirrorImg) {
  if (!(mirrorImg instanceof HTMLImageElement)) return false

  const canonicalSrc = mirrorImg.dataset.deferredMirrorSrc
  if (!canonicalSrc) return false
  if (mirrorImg.dataset.deferredLoaded === 'true') return true

  const loaded = findLoadedDeferredImageForCanonical(canonicalSrc)
  if (!loaded) return false

  applyLoadedDeferredState(
    mirrorImg,
    loaded.src,
    loaded.srcset || '',
    loaded.sizes || ''
  )
  return true
}

/**
 * @param {ParentNode} [root=document]
 */
export function hydrateDeferredMirrors(root = document) {
  for (const node of root.querySelectorAll('img[data-deferred-mirror-src]')) {
    if (node instanceof HTMLImageElement) {
      hydrateDeferredMirror(node)
    }
  }
}

/**
 * @param {HTMLImageElement} img
 * @returns {boolean}
 */
export function shouldObserveDeferredImage(img) {
  if (img.dataset.deferredLoaded === 'true') return false
  if (!img.dataset.deferredSrc) return false
  if (isDeferredCloneImage(img)) return false
  return true
}

/**
 * @param {HTMLImageElement} img
 * @param {string} src
 * @param {string} [srcset]
 * @param {string} [sizes]
 */
export function applyLoadedDeferredState(img, src, srcset, sizes) {
  const canonical =
    img.dataset.deferredSrc ||
    img.dataset.deferredMirrorSrc ||
    img.dataset.deferredCanonicalSrc ||
    src

  if (canonical) {
    img.dataset.deferredCanonicalSrc = canonical
  }

  if (sizes) {
    img.sizes = sizes
  }

  if (srcset) {
    img.srcset = srcset
  }

  img.src = src

  delete img.dataset.deferredSrc
  delete img.dataset.deferredSrcset
  delete img.dataset.deferredSizes
  delete img.dataset.deferredMirrorSrc

  img.dataset.deferredLoaded = 'true'
}

/**
 * @param {string} canonicalSrc
 * @param {string} src
 * @param {string} [srcset]
 * @param {string} [sizes]
 */
export function propagateDeferredImageLoad(canonicalSrc, src, srcset, sizes) {
  for (const node of document.querySelectorAll(
    'img[data-deferred-mirror-src]'
  )) {
    if (!(node instanceof HTMLImageElement)) continue
    if (
      !canonicalDeferredSrcMatches(node.dataset.deferredMirrorSrc, canonicalSrc)
    )
      continue
    applyLoadedDeferredState(node, src, srcset, sizes)
  }

  for (const node of document.querySelectorAll('img[data-deferred-src]')) {
    if (!(node instanceof HTMLImageElement)) continue
    if (!canonicalDeferredSrcMatches(node.dataset.deferredSrc, canonicalSrc))
      continue
    if (isDeferredCloneImage(node)) continue
    applyLoadedDeferredState(node, src, srcset, sizes)
  }
}

/** @type {boolean} */
let deferredImagesInitialized = false

/**
 * Vitest-only reset so initDeferredImages can be exercised per test.
 */
export function resetDeferredImagesInitForTesting() {
  deferredImagesInitialized = false
  defaultDeferredObserver = null
  highDeferredObserver = null
  inFlightLoads = 0
  loadQueue.length = 0
}

/**
 * @param {HTMLImageElement} img
 */
function markDeferredSectionHydrated(img) {
  const section = img.closest(
    '.media-bar-section, .creator-marquee, .banner-carousel'
  )
  if (section instanceof HTMLElement) {
    section.dataset.deferredSectionHydrated = 'true'
  }
}

function notifyDeferredImageLoaded(img) {
  hydrateDeferredMirrors(document)
  markDeferredSectionHydrated(img)
  document.dispatchEvent(
    new CustomEvent('deferred-image:loaded', { bubbles: true })
  )
}

/**
 * Immediately load deferred images within a root (e.g. active slideshow slide).
 * @param {ParentNode} [root]
 */
export function loadDeferredImagesIn(root) {
  if (!root) return

  for (const node of root.querySelectorAll('img[data-deferred-src]')) {
    if (node instanceof HTMLImageElement && shouldObserveDeferredImage(node)) {
      loadDeferredImage(node)
    }
  }

  hydrateDeferredMirrors(root)
}

/**
 * Double-rAF mirror hydrate after marquee clones are appended.
 * @param {ParentNode} [root=document]
 */
export function scheduleDeferredMirrorHydrate(root = document) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      hydrateDeferredMirrors(root)
      hydrateDeferredMirrors(document)
    })
  })
}

/**
 * Converts clone images to mirror placeholders that inherit loads from canonical imgs.
 * @param {ParentNode} root
 */
export function markDeferredImagesAsMirrors(root) {
  for (const node of root.querySelectorAll('img[data-deferred-src]')) {
    if (!(node instanceof HTMLImageElement)) continue

    node.dataset.deferredMirrorSrc = node.dataset.deferredSrc || ''
    delete node.dataset.deferredSrc
    delete node.dataset.deferredSrcset
    delete node.dataset.deferredSizes
  }

  hydrateDeferredMirrors(root)
}

/**
 * Loads a deferred image by swapping data attributes to real attributes
 * @param {HTMLImageElement} img - The image element to load
 */
function loadDeferredImage(img) {
  const src = img.dataset.deferredSrc
  const srcset = img.dataset.deferredSrcset
  const sizes = img.dataset.deferredSizes

  if (!src) return

  applyLoadedDeferredState(img, src, srcset, sizes)
  propagateDeferredImageLoad(src, src, srcset, sizes)
  notifyDeferredImageLoaded(img)
}

/**
 * Register deferred images under a root with the shared observers (post-init).
 * @param {ParentNode} root
 */
export function observeDeferredImagesIn(root) {
  if (!root || !deferredImagesInitialized || !defaultDeferredObserver) return

  for (const node of root.querySelectorAll('img[data-deferred-src]')) {
    if (node instanceof HTMLImageElement) {
      observeDeferredImage(node, defaultDeferredObserver, highDeferredObserver)
    }
  }
}

/** @type {IntersectionObserver | null} */
let defaultDeferredObserver = null

/** @type {IntersectionObserver | null} */
let highDeferredObserver = null

/** @type {number} */
let inFlightLoads = 0

/** @type {Array<{ img: HTMLImageElement, observer: IntersectionObserver }>} */
const loadQueue = []

/**
 * @param {{ img: HTMLImageElement, observer: IntersectionObserver }} item
 */
function enqueueDeferredLoad(item) {
  if (!shouldObserveDeferredImage(item.img)) return
  loadQueue.push(item)
  drainDeferredLoadQueue()
}

function drainDeferredLoadQueue() {
  const maxConcurrent = getDeferredImageMaxConcurrent()

  while (inFlightLoads < maxConcurrent && loadQueue.length > 0) {
    const next = loadQueue.shift()
    if (!next) break

    const { img, observer } = next
    if (!shouldObserveDeferredImage(img)) continue

    inFlightLoads += 1
    observer.unobserve(img)
    // Captured before loadDeferredImage deletes the data-deferred-* attributes,
    // so a failed load can be retried once against the same resource.
    const targetSrc = img.dataset.deferredSrc || ''
    loadDeferredImage(img)

    const release = () => {
      inFlightLoads -= 1
      drainDeferredLoadQueue()
    }

    let retried = false
    const handleError = () => {
      // A transient network/CDN hiccup should not leave the image stuck on its
      // placeholder. Retry the same resource once before giving up so the slot
      // still drains either way.
      if (!retried && targetSrc) {
        retried = true
        setTimeout(() => {
          img.addEventListener('load', release, { once: true })
          img.addEventListener('error', release, { once: true })
          img.src = targetSrc
        }, DEFERRED_IMAGE_RETRY_DELAY)
        return
      }
      release()
    }

    if (img.complete) {
      release()
    } else {
      img.addEventListener('load', release, { once: true })
      img.addEventListener('error', handleError, { once: true })
    }
  }
}

/**
 * @param {HTMLImageElement} img
 * @param {IntersectionObserver} defaultObserver
 * @param {IntersectionObserver} highObserver
 */
function observeDeferredImage(img, defaultObserver, highObserver) {
  if (!shouldObserveDeferredImage(img)) return

  const observer =
    img.dataset.deferredPriority === 'high' ? highObserver : defaultObserver
  observer.observe(img)
}

/**
 * Initialize deferred image loading with IntersectionObserver
 */
export function initDeferredImages() {
  if (deferredImagesInitialized) return
  deferredImagesInitialized = true

  if (!('IntersectionObserver' in window)) {
    const images = document.querySelectorAll('img[data-deferred-src]')
    for (const img of images) {
      if (img instanceof HTMLImageElement && shouldObserveDeferredImage(img)) {
        loadDeferredImage(img)
      }
    }
    hydrateDeferredMirrors(document)
    return
  }

  /**
   * @param {string} rootMargin
   */
  const createObserver = (rootMargin) =>
    new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const img = entry.target
          if (!(img instanceof HTMLImageElement)) continue
          enqueueDeferredLoad({ img, observer })
        }
      },
      {
        rootMargin,
        threshold: 0.01
      }
    )

  const defaultObserver = createObserver(
    getDeferredImageRootMarginForPriority('default')
  )
  const highObserver = createObserver(
    getDeferredImageRootMarginForPriority('high')
  )

  defaultDeferredObserver = defaultObserver
  highDeferredObserver = highObserver

  const images = document.querySelectorAll('img[data-deferred-src]')
  for (const img of images) {
    if (img instanceof HTMLImageElement) {
      observeDeferredImage(img, defaultObserver, highObserver)
    }
  }

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        // An in-place attribute change can (re)introduce data-deferred-src on an
        // existing node (e.g. a targeted morph, an app re-rendering markup, or
        // any other attribute mutation). childList/subtree observation never
        // sees this, so re-observe here to reload it.
        const target = mutation.target
        if (
          target instanceof HTMLImageElement &&
          shouldObserveDeferredImage(target)
        ) {
          observeDeferredImage(target, defaultObserver, highObserver)
        }
        continue
      }

      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLImageElement) {
          observeDeferredImage(node, defaultObserver, highObserver)
        } else if (node instanceof Element) {
          const nested = node.querySelectorAll('img[data-deferred-src]')
          for (const img of nested) {
            if (img instanceof HTMLImageElement) {
              observeDeferredImage(img, defaultObserver, highObserver)
            }
          }
        }
      }
    }
  })

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-deferred-src']
  })

  hydrateDeferredMirrors(document)
}

if (!import.meta.vitest) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeferredImages)
  } else {
    initDeferredImages()
  }
}

export { loadDeferredImage, PLACEHOLDER_SRC }
